import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/ui";
import { RealSchoolSettings } from "@/types/realSchoolSettings";
import { schoolSettingsService } from "@/services/school";
import { useGlobalRealtimeManager } from "@/hooks/realtime";
import { useAuth } from "@/hooks/auth";
import { useParams } from "react-router-dom";

export const useRealSchoolSettings = (schoolId: string = null) => {
  const [settings, setSettings] = useState<RealSchoolSettings | null>(null);
  const [flatedStages, setFlatedStages] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const { toast } = useToast();
  const { user, userProfile } = useAuth();
  const { schoolId: schooIdParam } = useParams();
  schoolId = schooIdParam || schoolId;

  console.log("[useRealSchoolSettings] Initializing with schoolId:", schoolId);
  console.log("[useRealSchoolSettings] User:", user?.id);
  console.log("[useRealSchoolSettings] UserProfile:", userProfile);

  const fetchSettings = useCallback(
    async (showLoading = true) => {
      console.log("[fetchSettings] Starting with schoolId:", schoolId, "showLoading:", showLoading);

      // Validate schoolId before fetching
      if (!schoolId || schoolId === "undefined") {
        console.error("[fetchSettings] ❌ Invalid schoolId:", schoolId);
        setLoading(false);
        return;
      }

      // تجنب عرض Loading إذا كانت الإعدادات موجودة مسبقاً
      if (showLoading && !settings) {
        setLoading(true);
      }

      try {
        console.log("[fetchSettings] Calling schoolSettingsService.fetchSettings");
        const fetchedSettings = await schoolSettingsService.fetchSettings(
          schoolId,
          user
        );

        console.log("[fetchSettings] ✅ Settings fetched successfully:", fetchedSettings);

        if (userProfile?.role === "teacher") {
          console.log("[fetchSettings] Filtering stages for teacher role");
          fetchedSettings.stages = fetchedSettings.stages?.map((stage: any) => {
            let stageGrades = stage.grades
              ? stage.grades.map((g: any) => {
                  const gradeSections = g.sections
                    ? g.sections?.filter((sec) =>
                        userProfile.sections?.includes(
                          `${stage?.id}$${g?.id}$${sec}`
                        )
                      )
                    : [];

                  return { ...g, sections: gradeSections };
                })
              : [];

           stageGrades= stageGrades?.filter((grade) => !(grade?.sections?.length === 0));

            return { ...stage, grades: stageGrades };
          });

          fetchedSettings.stages=fetchedSettings.stages?.filter(
            (stage) => !(stage?.grades?.length === 0)
          );

          setSettings(fetchedSettings);
        } else {
          console.log("[fetchSettings] Setting settings for non-teacher role");
          setSettings(fetchedSettings);
        }

        if (initialLoad) {
          setInitialLoad(false);
        }
      } catch (error) {
        console.error(
          "❌ useRealSchoolSettings: Error fetching school settings:",
          error,
          "schoolId:",
          schoolId
        );

        // عرض رسالة خطأ فقط إذا لم يكن هناك إعدادات محملة مسبقاً
        if (!settings && initialLoad) {
          // toast({
          //   title: "خطأ في تحميل الإعدادات",
          //   description: "لم نتمكن من تحميل إعدادات المدرسة",
          //   variant: "destructive",
          // });
        }
      } finally {
        // إخفاء Loading فقط إذا تم عرضه
        if (showLoading && !settings) {
          setLoading(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      initialLoad,
      schoolId,
      settings,
      user,
      userProfile?.role,
      userProfile?.sections,
    ]
  );

  const updateSettings = useCallback(
    async (updates: Partial<RealSchoolSettings>) => {
      console.log("somebody need to update the school settings: ");
      // try {
      // لا نعين loading هنا لتجنب إعادة تحميل الصفحة
      const updatedSettings = await schoolSettingsService.updateSettings(
        { ...updates, school_id: schoolId },
        settings
      );
      setSettings(updatedSettings);

      // إذا تم تحديث اسم المدرسة، نجبر تحديث الصفحة لضمان ظهور التغييرات في الهيدر
      if (
        updates.school_name &&
        updates.school_name !== settings?.school_name
      ) {
        toast({
          title: "تم حفظ الإعدادات",
          description: "تم تحديث اسم المدرسة بنجاح. سيتم تحديث الصفحة...",
        });
        // تأخير قصير لعرض الرسالة ثم إعادة تحميل الصفحة
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        toast({
          title: "تم حفظ الإعدادات",
          description: "تم تحديث إعدادات المدرسة بنجاح",
        });
      }
      // } catch (error: any) {
      //   console.error("Error updating settings:", error);
      //   toast({
      //     title: "خطأ في الحفظ",
      //     description: error.message || "لم نتمكن من حفظ الإعدادات",
      //     variant: "destructive",
      //   });
      // }
    },
    [schoolId, settings, toast]
  );

  useEffect(() => {
    fetchSettings();
  }, []);

  return {
    settings,
    loading,
    updateSettings,
    refetch: fetchSettings,
    forceRefresh: useCallback(async () => {
      // مسح الكاش أولاً
      // schoolSettingsService.clearCache();
      // ثم إعادة جلب البيانات
      const refreshedSettings = await schoolSettingsService.forceRefresh();
      setSettings(refreshedSettings);
    }, []),
  };
};

// Export the type for backward compatibility
export type { RealSchoolSettings };
